import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { PenLine, RotateCcw, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Firma generica — gestisce sia firma cliente (kind="client") che firma dipendente (kind="employee").
 */
export default function SignaturePad({ job, canEdit, canClear, onSaved, kind = "client" }) {
  const isClient = kind === "client";
  const field = isClient ? "client" : "employee";
  const existing = job[`${field}_signature`];
  const signerName = job[`${field}_signer_name`];
  const signedAt = job[isClient ? "signed_at" : "employee_signed_at"];

  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [name, setName] = useState(isClient ? (job.client_signer_name || job.client_name || "") : "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth, h = c.clientHeight;
    c.width = w * ratio; c.height = h * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
  }, [existing]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - rect.left, y: (t ? t.clientY : e.clientY) - rect.top };
  };
  const start = (e) => { e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); setDrawing(true); };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); setHasInk(true); };
  const end = () => setDrawing(false);
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); };

  const endpoint = isClient ? `/jobs/${job.id}/signature` : `/jobs/${job.id}/employee-signature`;
  const label = isClient ? "Cliente" : "Dipendente";
  const accent = isClient ? "sky" : "indigo";

  const save = async () => {
    if (!hasInk) { toast.error("Disegna la firma prima di salvare"); return; }
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const body = isClient ? { signature: dataUrl, signer_name: name } : { signature: dataUrl };
      const { data } = await api.post(endpoint, body);
      toast.success(`Firma ${label.toLowerCase()} salvata`);
      onSaved?.(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const removeSignature = async () => {
    if (!confirm(`Rimuovere la firma ${label.toLowerCase()}?`)) return;
    try { const { data } = await api.delete(endpoint); toast.success("Firma rimossa"); onSaved?.(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (existing) {
    const border = isClient ? "border-emerald-200" : "border-indigo-200";
    const chip = isClient ? "text-emerald-700" : "text-indigo-700";
    return (
      <div className={`bg-white rounded-2xl border ${border} p-6`} data-testid={`${field}-signature-saved-panel`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className={`flex items-center gap-2 ${chip} font-display font-bold`}>
              <CheckCircle2 className="w-5 h-5" /> Firmato dal {label.toLowerCase()}
            </div>
            <div className="text-xs text-slate-500 font-mono mt-1">
              {signerName} · {signedAt ? new Date(signedAt).toLocaleString("it-IT") : ""}
            </div>
          </div>
          {canClear && (
            <button
              onClick={removeSignature}
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold inline-flex items-center gap-1"
              data-testid={`${field}-clear-signature-button`}
            >
              <Trash2 className="w-3.5 h-3.5" /> Rimuovi
            </button>
          )}
        </div>
        <div className="border border-slate-200 bg-slate-50 rounded-xl p-4 flex items-center justify-center">
          <img src={existing} alt={`Firma ${label}`} className="max-h-40 object-contain" data-testid={`${field}-signature-image`} />
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400 italic">
        Firma {label.toLowerCase()} non ancora acquisita.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`${field}-signature-pad`}>
      <div className="flex items-center gap-2 mb-4">
        <PenLine className={`w-5 h-5 text-${accent}-600`} />
        <h3 className="font-display font-bold text-slate-900">Firma {label}</h3>
      </div>
      <div className="space-y-3">
        {isClient && (
          <div>
            <Label className="text-xs">Nome firmatario</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome e cognome"
              className="mt-1"
              data-testid={`${field}-signer-name-input`}
            />
          </div>
        )}
        <div className="relative rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden" style={{ height: 180 }}>
          <canvas
            ref={canvasRef}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className="w-full h-full touch-none cursor-crosshair"
            data-testid={`${field}-signature-canvas`}
          />
          {!hasInk && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-300 text-sm font-semibold">
              Firma qui con il dito o il mouse
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button type="button" variant="outline" onClick={clear} data-testid={`${field}-clear-canvas-button`}>
            <RotateCcw className="w-4 h-4 mr-2" /> Pulisci
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving || !hasInk}
            className={`bg-${accent}-600 hover:bg-${accent}-700 text-white`}
            data-testid={`${field}-save-signature-button`}
          >
            {saving ? "Salvataggio…" : "Salva Firma"}
          </Button>
        </div>
      </div>
    </div>
  );
}
